import {
  SpaceBetween,
  FormField,
  Box,
  Input,
  Button,
  ColumnLayout,
} from "@cloudscape-design/components-themed";
import { isEqual, isNull, isUndefined } from "lodash";
import { Dispatch, SetStateAction, useState, useEffect } from "react";
import {
  useGetCorpInfoByCorpNumQuery,
  useGetAddressByPostalCodeQuery,
} from "../../../features/api";
import { BankAccountsUpdateRequest } from "../../../features/api-interfaces";
import { COMMON_CONFIGS, COMMON_LABELS } from "../../default-constants";
import { IErrMsg, LABELS, CONSTRAINT_TEXT } from "../constants";
import {
  hasCorpInfo,
  validCorporateNumber,
  validCompanyName,
  validCompanyNameKana,
  validPostalCode,
  validPaymentTargetCompanyAddress2,
  validPaymentTargetCompanyAddress1Kana,
  validPhoneNumber,
} from "../validation";

interface IPaymentTargetCompanyUpdate {
  editingRequest?: BankAccountsUpdateRequest;
  setEditingRequest?: Dispatch<SetStateAction<BankAccountsUpdateRequest>>;
  errMsg?: IErrMsg;
  setErrMsg?: Dispatch<SetStateAction<IErrMsg>>;
}

/**
 * 入金口座情報照会・更新 入金先事業者情報 更新
 */
export const PaymentTargetCompanyUpdate = (
  props: IPaymentTargetCompanyUpdate
) => {
  const { editingRequest, setEditingRequest, errMsg, setErrMsg } = props;
  const hasCorpNo = !isNull(editingRequest?.corporateNumber);
  const isRequired = hasCorpNo || hasCorpInfo(editingRequest);
  const [isCorpNoSearchSkip, setIsCorpNoSearchSkip] = useState(true);
  const [isPostalCodeSearchSkip, setIsPostalCodeSearchSkip] = useState(true);
  const { data: corpInfo } = useGetCorpInfoByCorpNumQuery(
    editingRequest?.corporateNumber!,
    {
      skip: isCorpNoSearchSkip,
    }
  );
  const { data: address } = useGetAddressByPostalCodeQuery(
    editingRequest?.postalCode!,
    {
      skip: isPostalCodeSearchSkip,
    }
  );

  useEffect(() => {
    if (editingRequest && setEditingRequest) {
      setIsCorpNoSearchSkip(true);
      if (
        corpInfo &&
        isEqual(corpInfo.status, COMMON_CONFIGS.API.STATUS_SUCCESS)
      ) {
        // 法人番号検索APIで取得した事業者名と事業者名カナをセットする
        const { companyName, companyNameKana } = corpInfo.companyInfo;
        setEditingRequest({ ...editingRequest, companyName, companyNameKana });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpInfo]);

  useEffect(() => {
    if (editingRequest && setEditingRequest) {
      setIsPostalCodeSearchSkip(true);
      if (
        address &&
        isEqual(address.status, COMMON_CONFIGS.API.STATUS_SUCCESS)
      ) {
        const { prfNm, ctyOrCnt, lrgLtt, chr } = address.address;
        setEditingRequest({
          ...editingRequest,
          paymentTargetCompanyAddress1: (
            prfNm +
            ctyOrCnt +
            lrgLtt +
            chr
          ).trim(),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  /**
   * 法人番号の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const corporateNumberOnChange = (inputValue: string) => {
    if (editingRequest && setEditingRequest && setErrMsg) {
      // 法人番号を編集した場合は事業者名、事業者名カナを空にする
      setEditingRequest({
        ...editingRequest,
        corporateNumber: inputValue,
        companyName: "",
        companyNameKana: "",
      });
      setErrMsg({
        ...errMsg,
        corporateNumber: validCorporateNumber(inputValue),
      });
    }
  };

  /**
   * 法人番号検索ボタンを押したときの処理.
   */
  const corpNoSearchOnClick = () => {
    if (hasCorpNo && isUndefined(errMsg?.corporateNumber)) {
      setIsCorpNoSearchSkip(false);
    }
  };

  /**
   * 事業者名の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const companyNameOnChange = (inputValue: string) => {
    if (editingRequest && setEditingRequest && setErrMsg) {
      setEditingRequest({ ...editingRequest, companyName: inputValue });
      setErrMsg({
        ...errMsg,
        companyName: validCompanyName(inputValue, editingRequest),
      });
    }
  };

  /**
   * 事業者名カナの入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const companyNameKanaOnChange = (inputValue: string) => {
    if (editingRequest && setEditingRequest && setErrMsg) {
      setEditingRequest({ ...editingRequest, companyNameKana: inputValue });
      setErrMsg({
        ...errMsg,
        companyNameKana: validCompanyNameKana(inputValue, editingRequest),
      });
    }
  };

  /**
   * 郵便番号の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const postalCodeOnChange = (inputValue: string) => {
    if (editingRequest && setEditingRequest && setErrMsg) {
      setEditingRequest({
        ...editingRequest,
        postalCode: inputValue,
      });
      setErrMsg({
        ...errMsg,
        postalCode: validPostalCode(inputValue, editingRequest),
      });
    }
  };

  /**
   * 郵便番号検索ボタンを押したときの処理.
   */
  const postalCodeSearchOnClick = () => {
    if (
      !isNull(editingRequest?.postalCode) &&
      isUndefined(errMsg?.postalCode)
    ) {
      setIsPostalCodeSearchSkip(false);
    }
  };

  /**
   * 本社住所（その他）の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const paymentTargetCompanyAddress2OnChange = (inputValue: string) => {
    if (editingRequest && setEditingRequest && setErrMsg) {
      setEditingRequest({
        ...editingRequest,
        paymentTargetCompanyAddress2: inputValue,
      });
      setErrMsg({
        ...errMsg,
        paymentTargetCompanyAddress2: validPaymentTargetCompanyAddress2(
          inputValue,
          editingRequest
        ),
      });
    }
  };

  /**
   * 本社住所カナの入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const paymentTargetCompanyAddress1Kana = (inputValue: string) => {
    if (editingRequest && setEditingRequest && setErrMsg) {
      setEditingRequest({
        ...editingRequest,
        paymentTargetCompanyAddress1Kana: inputValue,
      });
      setErrMsg({
        ...errMsg,
        paymentTargetCompanyAddress1Kana: validPaymentTargetCompanyAddress1Kana(
          inputValue,
          editingRequest
        ),
      });
    }
  };

  /**
   * 電話番号の入力ハンドラ.
   * @param {string} inputValue 入力値
   */
  const phoneNumberOnChange = (inputValue: string) => {
    if (editingRequest && setEditingRequest && setErrMsg) {
      setEditingRequest({ ...editingRequest, phoneNumber: inputValue });
      setErrMsg({
        ...errMsg,
        phoneNumber: validPhoneNumber(inputValue, editingRequest),
      });
    }
  };

  return (
    <SpaceBetween direction={"vertical"} size={"l"}>
      <FormField
        label={<Box variant={"strong"}>{LABELS.CORPORATE_NUMBER}</Box>}
        constraintText={CONSTRAINT_TEXT.CORPORATE_NUMBER}
        errorText={errMsg?.corporateNumber}
      >
        <SpaceBetween direction={"horizontal"} size={"l"}>
          <Input
            value={editingRequest?.corporateNumber ?? ""}
            onChange={({ detail: { value } }) => corporateNumberOnChange(value)}
          />

          <Button
            formAction={"none"}
            variant={"normal"}
            onClick={corpNoSearchOnClick}
          >
            {COMMON_LABELS.BTNS.SEARCH_CORP_NUM}
          </Button>
        </SpaceBetween>
      </FormField>
      <ColumnLayout columns={4}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.COMPANY_NAME}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          constraintText={CONSTRAINT_TEXT.COMPANY_NAME}
          errorText={errMsg?.companyName}
        >
          <Input
            value={editingRequest?.companyName ?? ""}
            disabled={hasCorpNo}
            onChange={({ detail: { value } }) => companyNameOnChange(value)}
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={4}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.COMPANY_NAME_KANA}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          constraintText={CONSTRAINT_TEXT.COMPANY_NAME_KANA}
          errorText={errMsg?.companyNameKana}
        >
          <Input
            value={editingRequest?.companyNameKana ?? ""}
            disabled={hasCorpNo}
            onChange={({ detail: { value } }) => companyNameKanaOnChange(value)}
          />
        </FormField>
      </ColumnLayout>
      <FormField
        label={
          <SpaceBetween direction={"horizontal"} size={"xs"}>
            <Box variant={"strong"}>{LABELS.POSTAL_CODE}</Box>
            {isRequired && (
              <Box variant={"small"} color={"text-status-error"}>
                {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
              </Box>
            )}
          </SpaceBetween>
        }
        constraintText={CONSTRAINT_TEXT.POSTAL_CODE}
        errorText={errMsg?.postalCode}
      >
        <SpaceBetween direction={"horizontal"} size={"l"}>
          <Input
            value={editingRequest?.postalCode ?? ""}
            onChange={({ detail: { value } }) => postalCodeOnChange(value)}
          />
          <Button
            formAction={"none"}
            variant={"normal"}
            onClick={postalCodeSearchOnClick}
          >
            {COMMON_LABELS.BTNS.SEARCH_POSTAL_CODE}
          </Button>
        </SpaceBetween>
      </FormField>
      <ColumnLayout columns={2}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.ADDRESS}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          errorText={errMsg?.paymentTargetCompanyAddress1}
        >
          <Input
            value={editingRequest?.paymentTargetCompanyAddress1 ?? ""}
            disabled={true}
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={2}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.ADDRESS_OTHERS}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          constraintText={CONSTRAINT_TEXT.ADDRESS_OTHERS}
          errorText={errMsg?.paymentTargetCompanyAddress2}
        >
          <Input
            value={editingRequest?.paymentTargetCompanyAddress2 ?? ""}
            onChange={({ detail: { value } }) =>
              paymentTargetCompanyAddress2OnChange(value)
            }
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={2}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.ADDRESS_KANA}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          constraintText={CONSTRAINT_TEXT.ADDRESS_KANA}
          errorText={errMsg?.paymentTargetCompanyAddress1Kana}
        >
          <Input
            value={editingRequest?.paymentTargetCompanyAddress1Kana ?? ""}
            onChange={({ detail: { value } }) =>
              paymentTargetCompanyAddress1Kana(value)
            }
          />
        </FormField>
      </ColumnLayout>
      <ColumnLayout columns={4}>
        <FormField
          label={
            <SpaceBetween direction={"horizontal"} size={"xs"}>
              <Box variant={"strong"}>{LABELS.PHONE_NUMBER}</Box>
              {isRequired && (
                <Box variant={"small"} color={"text-status-error"}>
                  {COMMON_LABELS.INPUT_ITEM_MESSAGE.REQUIRED}
                </Box>
              )}
            </SpaceBetween>
          }
          stretch={true}
          constraintText={CONSTRAINT_TEXT.PHONE_NUMBER}
          errorText={errMsg?.phoneNumber}
        >
          <Input
            value={editingRequest?.phoneNumber ?? ""}
            onChange={({ detail: { value } }) => phoneNumberOnChange(value)}
          />
        </FormField>
      </ColumnLayout>
    </SpaceBetween>
  );
};
